import React, { useEffect, useRef, useState } from "react";
import Reveal from "../site/Reveal";
import { SectionLabel, ArrowLink } from "../site/Bits";
import { VERTICALS } from "../../lib/content";
import { IMAGES } from "../../lib/images";

/** one distinct photograph per vertical - shown in the sticky panel */
const PANEL_IMAGE = {
  events: { src: IMAGES.verticals.events, alt: "Live event production for a packed auditorium" },
  advertising: { src: IMAGES.verticals.advertisingA, alt: "Brand campaign production" },
  "social-impact": {
    src: IMAGES.verticals.socialImpact,
    alt: "Community social-impact initiative supported by LYKIT",
  },
  "ott-digital": { src: IMAGES.verticals.ott, alt: "Streaming platforms and digital entertainment" },
  "post-production": { src: IMAGES.verticals.postProduction, alt: "Post-production edit suite" },
};

const Num = ({ children }) => (
  <span
    aria-hidden="true"
    className="outline-number block"
    style={{ fontSize: "clamp(40px,4.6vw,68px)" }}
  >
    {children}
  </span>
);

const ServiceList = ({ items }) => (
  <ul
    className="mt-5 grid gap-x-8 gap-y-0 sm:grid-cols-2"
    style={{ listStyle: "none", padding: 0, margin: 0 }}
  >
    {items.map((item) => (
      <li
        key={item}
        className="py-[8px]"
        style={{
          borderTop: "1px solid rgba(23,21,34,0.10)",
          fontSize: 13.5,
          lineHeight: 1.4,
          color: "rgba(23,21,34,0.76)",
        }}
      >
        {item}
      </li>
    ))}
  </ul>
);

export default function Verticals() {
  const [active, setActive] = useState(0);
  const blockRefs = useRef([]);

  useEffect(() => {
    const nodes = blockRefs.current.filter(Boolean);
    if (!nodes.length || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = Number(entry.target.dataset.index);
          if (!Number.isNaN(index)) setActive(index);
        });
      },
      // a thin band across the middle of the viewport decides the active service
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 },
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="lk-section"
      data-testid="five-verticals-section"
      id="verticals"
      data-no-reveal
    >
      <div className="lk-container-wide">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Reveal>
              <SectionLabel>What We Do</SectionLabel>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="display-lg mt-4">
                <span className="block">FIVE VERTICALS.</span>
                <span className="block" style={{ color: "#352A59" }}>
                  ONE VISION.
                </span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.16}>
            <p className="body-muted max-w-[38ch] text-[14px] leading-[1.6]">
              One creative ecosystem across events, advertising, social impact, digital entertainment and
              post-production.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="lk-container-wide mt-[clamp(26px,2.6vw,44px)]">
        <div className="grid lg:grid-cols-12 lg:gap-x-14">
          {/* ------------------------------------------ scrolling service list */}
          <div className="lg:col-span-6">
            {VERTICALS.map((vertical, index) => {
              const visual = PANEL_IMAGE[vertical.slug];
              return (
                <article
                  key={vertical.id}
                  ref={(node) => {
                    blockRefs.current[index] = node;
                  }}
                  data-index={index}
                  data-testid={`vertical-block-${vertical.id}`}
                  className="py-[clamp(26px,2.8vw,44px)] first:pt-0"
                  style={{ borderTop: index === 0 ? "none" : "1px solid rgba(23,21,34,0.10)" }}
                >
                  <Reveal>
                    <Num>{vertical.num}</Num>
                    <h3 className="display-md mt-2">{vertical.title}</h3>
                    <p className="lead mt-4 max-w-[46ch]">{vertical.description}</p>
                  </Reveal>

                  {/* mobile / tablet: the image travels with its own block */}
                  <Reveal variant="image" className="mt-6 lg:hidden">
                    <div className="img-frame img-zoom w-full" style={{ aspectRatio: "4 / 3" }}>
                      <img src={visual.src} alt={visual.alt} loading="lazy" />
                    </div>
                  </Reveal>

                  <Reveal delay={0.08}>
                    <ServiceList items={vertical.services} />
                    <div className="mt-6">
                      <ArrowLink to={vertical.to} data-testid={`vertical-${vertical.id}-cta`}>
                        {vertical.cta}
                      </ArrowLink>
                    </div>
                  </Reveal>
                </article>
              );
            })}
          </div>

          {/* --------------------------------- sticky, crossfading image panel */}
          <div className="hidden lg:col-span-5 lg:col-start-8 lg:block">
            <div className="sticky" style={{ top: "clamp(96px, 12vh, 124px)" }}>
              <div
                className="img-frame relative w-full overflow-hidden"
                style={{ height: "min(68vh, 620px)" }}
                data-testid="verticals-sticky-panel"
              >
                {VERTICALS.map((vertical, index) => {
                  const visual = PANEL_IMAGE[vertical.slug];
                  const isActive = index === active;
                  return (
                    <img
                      key={vertical.id}
                      src={visual.src}
                      alt={isActive ? visual.alt : ""}
                      aria-hidden={!isActive}
                      loading="lazy"
                      data-testid={`verticals-panel-image-${vertical.id}`}
                      className="absolute inset-0 block h-full w-full object-cover"
                      style={{
                        opacity: isActive ? 1 : 0,
                        transition: "opacity 700ms var(--ease-out)",
                      }}
                    />
                  );
                })}

                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-0 z-10"
                  style={{
                    height: "42%",
                    background: "linear-gradient(180deg, rgba(14,13,20,0) 0%, rgba(14,13,20,0.72) 100%)",
                  }}
                />

                <div className="absolute inset-x-0 bottom-0 z-20 flex items-end justify-between gap-5 p-6">
                  <span
                    className="project-meta"
                    data-testid="verticals-panel-caption"
                    style={{ color: "rgba(247,246,242,0.9)" }}
                  >
                    {VERTICALS[active].num} — {VERTICALS[active].title}
                  </span>
                  <span className="flex items-center gap-[6px]" aria-hidden="true">
                    {VERTICALS.map((vertical, index) => (
                      <span
                        key={vertical.id}
                        className="block h-[2px]"
                        style={{
                          width: index === active ? 22 : 10,
                          background:
                            index === active ? "rgba(216,183,122,0.95)" : "rgba(247,246,242,0.34)",
                          transition: "width 400ms var(--ease-out), background-color 400ms var(--ease-out)",
                        }}
                      />
                    ))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
